// src/pages/LandingPage.jsx — دكتور حمدي صالح — Exact Reference Design
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as d1Api from '../services/d1Api';
import {
    CheckCircle,
    Video,
    Headphones,
    BookOpen,
    Target,
    Clock,
    LogIn,
    UserPlus,
    ArrowLeft,
    X,
    Award,
    Youtube,
    Facebook,
    MessageCircle,
    Bell,
    ShoppingCart,
    User,
    Trophy,
    Instagram
} from 'lucide-react';
import { Footer } from '../components/common/Footer';
import { FloatingWhatsApp } from '../components/common/FloatingWhatsApp';

// Custom TikTok icon
const TikTokIcon = ({ size = 24, className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/>
    </svg>
);

// WhatsApp icon
const WhatsAppIcon = ({ size = 24, className = '' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

export const LandingPage = () => {
    const [courses, setCourses] = useState([]);
    const [topStudents, setTopStudents] = useState([]);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const { user } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
            const currentProgress = (window.scrollY / totalScroll) * 100;
            setScrollProgress(currentProgress);
        };
        window.addEventListener('scroll', handleScroll);

        // Fetch live courses from D1 API
        const fetchCourses = async () => {
            try {
                const res = await d1Api.getAllCourses();
                if (res.success && res.data && res.data.length > 0) {
                    setCourses(res.data);
                } else {
                    setCourses([]);
                }
            } catch (err) {
                console.error('Error fetching courses:', err);
                setCourses([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();

        // Fetch leaderboard
        const fetchLeaderboard = async () => {
            try {
                const res = await d1Api.getLeaderboard();
                if (res.success && res.data && res.data.length > 0) {
                    setTopStudents(res.data.map(s => ({
                        id: s.id,
                        name: [s.firstName, s.middleName, s.lastName].filter(Boolean).join(' '),
                        points: s.points || 0,
                        rank: s.grade === '1sec' ? 'أولى ثانوي' : s.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'
                    })));
                } else {
                    setTopStudents([]);
                }
            } catch {
                setTopStudents([]);
            }
        };
        fetchLeaderboard();

        const timer = setTimeout(() => setShowHelpMessage(true), 10000);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timer);
        };
    }, []);

    const features = [
        { icon: CheckCircle, title: 'مرونة في المذاكرة', desc: 'ذاكر في أي وقت ومن أي مكان يناسبك' },
        { icon: Video, title: 'فيديوهات بشرح مبسط', desc: 'شرح وافي ومبسط لكل جزئيات المنهج' },
        { icon: Target, title: 'اختبارات مستمرة', desc: 'تقييم دوري لمستواك مع تصحيح فوري' },
        { icon: Headphones, title: 'دعم فني ومجتمع طلابي', desc: 'تواصل مع زملائك واستفسر في أي وقت' },
        { icon: BookOpen, title: 'مصادر متعددة', desc: 'حل من كل المصادر مش من مصدر واحد' },
    ];

    const grades = [
        { id: '1sec', label: 'بكالوريا و ثانوي عام', tag: 'تاريخ أولى ثانوي' },
        { id: '2sec', label: 'بكالوريا و ثانوي عام', tag: 'تاريخ تانية ثانوي' },
        { id: '3sec', label: 'جزء الجيولوجيا', tag: 'للصف الثالث الثانوي' },
    ];

    const filteredCourses = selectedGrade
        ? courses.filter(c => c.grade === selectedGrade && c.status !== 'hidden')
        : courses.filter(c => c.status !== 'hidden');

    if (loading) {
        return (
            <div className="min-h-screen bg-science-pattern flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-[#2D0A59] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-[#2D0A59] font-bold text-lg">جاري التحميل...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative overflow-x-hidden bg-science-pattern" dir="rtl">

            {/* ── SCROLL PROGRESS BAR ── */}
            <div className="fixed top-0 right-0 left-0 h-1 z-[999]">
                <div
                    className="h-full transition-all duration-150"
                    style={{ width: `${scrollProgress}%`, background: 'linear-gradient(90deg, #2D0A59, #E5D31D)' }}
                />
            </div>

            {/* ══════════════════════════════════════
                TOP YELLOW BANNER — Aref Ruqaa
            ══════════════════════════════════════ */}
            <div className="w-full py-2.5 flex items-center justify-center" style={{ background: '#E5D31D' }}>
                <p className="text-black font-bold text-xl md:text-2xl aref-ruqaa tracking-wide">
                    لَا أَبْرَحُ حَتَّىٰ أَبْلُغَ
                </p>
            </div>

            {/* ══════════════════════════════════════
                STICKY NAVBAR — White
            ══════════════════════════════════════ */}
            <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 p-2 md:p-3">
                <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between">
                    
                    {/* RIGHT: Logo (Text Only in Aref Ruqaa) */}
                    <div className="flex items-center gap-2">
                        <span className="text-[26px] md:text-3xl text-[#2D0A59] select-none" style={{fontFamily: "'Aref Ruqaa', serif", fontWeight: 700}}>
                            د. حمدي صالح
                        </span>
                    </div>

                    {/* LEFT: Actions */}
                    <div className="flex items-center gap-2 order-2 md:order-3">
                        <button className="p-2 text-gray-500 hover:text-[#2D0A59] transition hidden md:block">
                            <Bell size={20} />
                        </button>
                        <button className="p-2 text-gray-500 hover:text-[#2D0A59] transition hidden md:block">
                            <ShoppingCart size={20} />
                        </button>

                        {user ? (
                            <button
                                onClick={() => window.location.href = user?.role === 'admin' ? '/admin' : '/student'}
                                className="bg-[#2D0A59] hover:bg-[#4A1A8A] text-white px-5 py-2 rounded-full font-bold transition flex items-center gap-2 shadow-lg text-sm"
                            >
                                <ArrowLeft size={15} />
                                حسابي
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => setShowLoginModal(true)}
                                    className="text-[#2D0A59] hover:text-[#4A1A8A] font-bold text-sm transition hidden md:flex items-center gap-1"
                                >
                                    <ArrowLeft size={15} />
                                    تسجيل الدخول
                                </button>
                                <button
                                    onClick={() => window.location.href = '/login?tab=register'}
                                    className="text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-2 shadow-md hover:scale-105 transition-transform"
                                    style={{ background: '#2D0A59' }}
                                >
                                    <User size={15} />
                                    اشترك الان
                                </button>
                            </>
                        )}
                    </div>

                    {/* CENTER: Nav links (Horizontal scroll on mobile) */}
                    <nav className="flex w-full md:w-auto order-3 md:order-2 overflow-x-auto hide-scrollbar gap-5 text-[13px] md:text-sm font-bold text-gray-600 mt-2 pt-2 border-t border-gray-100 md:mt-0 md:pt-0 md:border-t-0 justify-center whitespace-nowrap">
                        <a href="#" className="hover:text-[#2D0A59] transition">الرئيسية</a>
                        <a href="#courses" className="hover:text-[#2D0A59] transition">الكورسات</a>
                        <a href="#features" className="hover:text-[#2D0A59] transition">المميزات</a>
                        <a href="#honor" className="hover:text-[#2D0A59] transition">لوحة الشرف</a>
                    </nav>
                </div>
            </header>

            {/* ══════════════════════════════════════
                HERO — Centered layout (matches reference images exactly)
            ══════════════════════════════════════ */}
            <section className="relative z-10 pt-8 pb-0">
                <div className="container mx-auto px-4 flex flex-col items-center text-center">

                    {/* Doctor blob with ring and pop-out head */}
                    <div className="hero-blob-wrap">
                        {/* Detached gold ring */}
                        <div className="blob-ring"></div>
                        {/* Purple oval blob */}
                        <div className="hero-blob-large">
                            <img
                                src="/images/doctor-hero.png"
                                alt="دكتور حمدي صالح"
                                onError={(e) => { e.target.src = '/images/image_bb8b98.jpg'; }}
                            />
                        </div>
                    </div>

                    {/* Text below blob */}
                    <div className="mt-6 max-w-lg mx-auto px-4">
                        {/* "منصة دكتور" */}
                        <h2 className="text-2xl md:text-3xl font-bold text-[#2D0A59] mb-1 cairo">
                            منصة دكتور
                        </h2>
                        {/* "حمدي صالـح" */}
                        <h1 className="text-5xl md:text-7xl font-black leading-tight mb-4 cairo"
                            style={{ color: '#2D0A59' }}>
                            حَمْدِي صَالِح
                        </h1>

                        {/* Yellow full-width subtitle label */}
                        <div className="w-full py-3 px-4 mb-6 rounded-lg"
                            style={{ background: '#E5D31D' }}>
                            <p className="text-[#2D0A59] font-extrabold text-lg md:text-xl cairo">
                                تاريخ
                            </p>
                        </div>

                        {/* CTA Button */}
                        <button
                            onClick={() => setShowLoginModal(true)}
                            className="inline-flex items-center gap-3 text-white font-black text-lg px-10 py-4 rounded-full shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
                            style={{ background: '#2D0A59' }}
                        >
                            <User size={22} />
                            يلا سجل حسابك
                        </button>
                    </div>
                </div>
            </section>

            {/* ══════════════════════════════════════
                WAVE DIVIDER → Purple section
            ══════════════════════════════════════ */}
            <div className="relative mt-8 -mb-1 z-10" style={{ lineHeight: 0 }}>
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg"
                    className="w-full" preserveAspectRatio="none" style={{ height: '80px' }}>
                    {/* Background white */}
                    <path d="M0 0 H1440 V60 C1080 120 720 80 360 100 C180 110 80 90 0 80 Z"
                        fill="#FAFAFA" />
                    {/* Light purple layer */}
                    <path d="M0 30 C300 80 600 40 900 70 C1100 90 1300 60 1440 50 L1440 120 L0 120 Z"
                        fill="rgba(196,176,238,0.35)" />
                    {/* Deep purple */}
                    <path d="M0 60 C200 100 500 70 800 90 C1000 105 1250 85 1440 75 L1440 120 L0 120 Z"
                        fill="#2D0A59" />
                </svg>
            </div>

            {/* ══════════════════════════════════════
                GRADE SELECTION — Purple bg
            ══════════════════════════════════════ */}
            <section className="relative z-10 py-16" style={{ background: '#2D0A59' }}>
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-3 cairo">
                            اختر <span style={{ color: '#E5D31D' }}>صفك الدراسي</span>
                        </h2>
                        <p className="text-white/60 max-w-xl mx-auto cairo">
                            اختر المرحلة الدراسية لعرض الكورسات المتاحة ليك
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                        {grades.map((grade) => (
                            <div
                                key={grade.id}
                                onClick={() => setSelectedGrade(selectedGrade === grade.id ? null : grade.id)}
                                className="rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:-translate-y-2"
                                style={{
                                    background: selectedGrade === grade.id
                                        ? '#E5D31D'
                                        : 'rgba(255,255,255,0.08)',
                                    border: `2px solid ${selectedGrade === grade.id ? '#E5D31D' : 'rgba(229,211,29,0.2)'}`,
                                    boxShadow: selectedGrade === grade.id ? '0 10px 40px rgba(229,211,29,0.3)' : 'none'
                                }}
                            >
                                <div className="px-4 py-2 rounded-xl flex items-center justify-center mx-auto mb-4 w-max min-w-[120px] min-h-[64px]"
                                    style={{
                                        background: selectedGrade === grade.id ? '#2D0A59' : '#E5D31D',
                                        boxShadow: '0 8px 20px rgba(229,211,29,0.3)'
                                    }}>
                                    <span className="text-base md:text-lg font-black"
                                        style={{ color: selectedGrade === grade.id ? '#E5D31D' : '#2D0A59' }}>
                                        {grade.tag}
                                    </span>
                                </div>
                                <h3 className="text-xl font-black cairo"
                                    style={{ color: selectedGrade === grade.id ? '#2D0A59' : '#FFFFFF' }}>
                                    {grade.label}
                                </h3>
                                {selectedGrade === grade.id && (
                                    <div className="mt-2 inline-block px-3 py-0.5 rounded-full text-xs font-black"
                                        style={{ background: '#2D0A59', color: '#E5D31D' }}>
                                        ✓ محدد
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Wave up back to white */}
            <div className="relative -mt-1 z-10" style={{ lineHeight: 0, background: '#2D0A59' }}>
                <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg"
                    className="w-full" preserveAspectRatio="none" style={{ height: '60px' }}>
                    <path d="M0 80 C360 0 720 60 1080 20 C1260 0 1380 40 1440 30 L1440 80 L0 80 Z"
                        fill="#FAFAFA" />
                </svg>
            </div>

            {/* ══════════════════════════════════════
                FEATURES SECTION
            ══════════════════════════════════════ */}
            <section id="features" className="relative z-10 py-16 bg-science-pattern">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-black text-[#2D0A59] mb-3 cairo">
                            ليه تختار <span style={{ color: '#E5D31D' }}>منصتنا</span>؟
                        </h2>
                        <p className="text-gray-500 max-w-xl mx-auto cairo">
                            منصتنا مش مجرد شرح، دي تجربة متكاملة عشان الطالب يحقق أعلى الدرجات
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {features.map((feature, idx) => (
                            <div key={idx} className="glass-card p-6 group cursor-pointer">
                                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all group-hover:rotate-3"
                                    style={{ background: 'rgba(45,10,89,0.08)' }}>
                                    <feature.icon size={26} style={{ color: '#2D0A59' }} />
                                </div>
                                <h3 className="text-lg font-black text-[#2D0A59] mb-2 cairo">{feature.title}</h3>
                                <p className="text-sm text-gray-500 leading-relaxed cairo">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════════════════════════════════
                COURSES SECTION
            ══════════════════════════════════════ */}
            <section id="courses" className="relative z-10 py-16 bg-science-pattern">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-black text-[#2D0A59] mb-3 cairo">
                            الكورسات <span style={{ color: '#E5D31D' }}>المتاحة</span>
                        </h2>
                        <p className="text-gray-500 max-w-xl mx-auto cairo">
                            اختر الكورس المناسب لمستواك وابدأ رحلة التميز
                        </p>
                    </div>

                    {filteredCourses.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                                style={{ background: 'rgba(45,10,89,0.08)' }}>
                                <BookOpen size={36} style={{ color: '#2D0A59' }} />
                            </div>
                            <p className="text-gray-400 font-bold cairo text-lg">لا توجد كورسات متاحة حالياً</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                            {filteredCourses.map((course) => {
                                const isLocked = course.status === 'locked';
                                return (
                                    <div
                                        key={course.id}
                                        className={`course-card ${isLocked ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
                                        onClick={() => {
                                            if (isLocked) return;
                                            if (!user) setShowLoginModal(true);
                                        }}
                                    >
                                        <div className="h-48 relative overflow-hidden"
                                            style={{ background: 'linear-gradient(135deg, #2D0A59, #4A1A8A)' }}>
                                            <img
                                                src={course.image_url || '/images/doctor-hero.png'}
                                                alt={course.title}
                                                className="w-full h-full object-cover opacity-80 transition duration-500 hover:opacity-95"
                                                onError={(e) => { e.target.src = '/images/image_bb8b98.jpg'; }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                                            {isLocked && (
                                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                    <div className="bg-white/90 px-4 py-2 rounded-xl font-bold text-sm text-[#2D0A59] cairo">
                                                        🔒 الحجز مغلق
                                                    </div>
                                                </div>
                                            )}
                                            <span className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-black cairo"
                                                style={{ background: '#E5D31D', color: '#2D0A59' }}>
                                                {course.grade === '1sec' ? 'أولى ثانوي' : course.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                                            </span>
                                            <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-xl font-black text-sm shadow-lg"
                                                style={{ background: '#FFFFFF', color: '#2D0A59' }}>
                                                {course.price} <span className="text-xs">ج.م</span>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <h3 className="text-lg font-black mb-2 text-[#2D0A59] cairo">{course.title}</h3>
                                            <p className="text-sm mb-4 line-clamp-2 text-gray-500 cairo">{course.description}</p>
                                            <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                                                <span className="text-sm flex items-center gap-1 text-gray-400 cairo">
                                                    <Clock size={14} />
                                                    {course.duration_days || course.duration || 30} يوم
                                                </span>
                                                {isLocked ? (
                                                    <span className="text-gray-400 font-bold text-sm cairo">🔒 مغلق</span>
                                                ) : (
                                                    <button className="bg-[#28A745] hover:bg-[#1e8449] text-white px-4 py-1.5 rounded-full font-black text-sm transition-all hover:scale-105 cairo">
                                                        اشترك الآن
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* ══════════════════════════════════════
                HONOR BOARD (LEADERBOARD)
            ══════════════════════════════════════ */}
            <section id="honor" className="relative z-10 py-16 bg-science-pattern">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-black text-[#2D0A59] mb-3 cairo">
                            🏆 لوحة <span style={{ color: '#E5D31D' }}>الشرف</span>
                        </h2>
                        <p className="text-gray-500 max-w-xl mx-auto cairo">
                            نخبة المتميزين في رحلتنا نحو التفوق
                        </p>
                    </div>

                    {topStudents.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                                style={{ background: 'rgba(45,10,89,0.08)' }}>
                                <Trophy size={40} style={{ color: '#2D0A59' }} />
                            </div>
                            <p className="text-gray-400 font-bold cairo text-lg">
                                قريباً... سيتم تحديد لوحة الشرف من قبل الإدارة
                            </p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            {topStudents.map((student, i) => (
                                <div key={student.id}
                                    className="bg-white border border-gray-100 p-6 text-center rounded-2xl hover:-translate-y-2 transition-transform shadow-lg relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-full h-1"
                                        style={{ background: 'linear-gradient(90deg, #2D0A59, #E5D31D)' }}></div>
                                    <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center border-4"
                                        style={{ background: 'rgba(45,10,89,0.08)', borderColor: '#E5D31D' }}>
                                        <span className="text-3xl">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                                    </div>
                                    <h3 className="text-xl font-black text-[#2D0A59] mb-2 cairo">{student.name}</h3>
                                    <div className="inline-block px-3 py-1 rounded-full text-sm font-bold mb-3 cairo"
                                        style={{ background: 'rgba(45,10,89,0.08)', color: '#2D0A59' }}>
                                        {student.rank}
                                    </div>
                                    <div className="text-2xl font-black cairo" style={{ color: '#E5D31D' }}>{student.points}</div>
                                    <div className="text-sm text-gray-400 cairo">نقطة</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Replace old footer with the new Footer component */}
            <Footer />

            {/* Login Modal */}
            {showLoginModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
                    onClick={() => setShowLoginModal(false)}>
                    <div className="bg-white p-8 max-w-md w-full animate-in rounded-2xl shadow-2xl relative overflow-hidden"
                        onClick={e => e.stopPropagation()}>
                        
                        <div className="absolute top-0 left-0 w-full h-1"
                            style={{ background: 'linear-gradient(90deg, #2D0A59, #E5D31D)' }}></div>

                        <button
                            onClick={() => setShowLoginModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
                        >
                            <X size={24} />
                        </button>

                        <div className="text-center mb-6 mt-2">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                                style={{ background: '#2D0A59' }}>
                                <img src="/images/logo.png" alt="Logo" className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="font-weight:900;color:#E5D31D;font-size:1.5rem">د</span>'; }} />
                            </div>
                            <h2 className="text-2xl font-black text-[#2D0A59] mb-1">
                                أهلاً بك في منصة دكتور حمدي صالح
                            </h2>
                            <p className="text-gray-500 cairo text-sm">سجل دخولك وابدأ رحلة التعلم</p>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={() => { setShowLoginModal(false); window.location.href = '/login'; }}
                                className="w-full text-white p-4 rounded-xl font-black flex items-center justify-center gap-3 transition hover:scale-[1.02] cairo shadow-lg"
                                style={{ background: '#2D0A59' }}
                            >
                                <LogIn size={20} />
                                الانتقال لتسجيل الدخول
                            </button>

                            <div className="flex items-center gap-3 py-1">
                                <div className="flex-1 h-px bg-gray-200"></div>
                                <span className="text-xs text-gray-400 font-bold cairo">أو</span>
                                <div className="flex-1 h-px bg-gray-200"></div>
                            </div>

                            <button
                                onClick={() => { setShowLoginModal(false); window.location.href = '/login?tab=register'; }}
                                className="w-full border-2 border-[#2D0A59] text-[#2D0A59] hover:bg-[#2D0A59] hover:text-white p-4 rounded-xl font-black flex items-center justify-center gap-3 transition-all hover:scale-[1.02] cairo"
                            >
                                <UserPlus size={20} />
                                إنشاء حساب جديد
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating WhatsApp Bubble */}
            <FloatingWhatsApp />
        </div>
    );
};
